/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { isEmpty } from '@ember/utils';
import CampaignModel from 'cio/models/campaign';
import ApiErrorsService from 'cio/services/api-errors';
import AlertsService from 'cio/services/alerts';
import AuthService from 'cio/services/auth';
import AnalyticsService from 'cio/services/analytics';
import { ComponentLike } from '@glint/template';

interface DetailsEditorArgs {
  campaign: CampaignModel;
  onSave: (name: string, description: string) => unknown;
  tagEditorComponent: ComponentLike<any>;
}

export default class DetailEditor extends Component<DetailsEditorArgs> {
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;

  @tracked isEditing = false;
  @tracked name = '';
  @tracked description = '';

  get isSaveDisabled() {
    return !isEmpty(this.errors) || (this.save as any).isRunning;
  }

  get errors() {
    let errors: any[] = [];

    if (!isEmpty((this.args.campaign.errors as Record<string, any>).name)) {
      errors.push((this.args.campaign.errors as Record<string, any>).name);
    }

    if (isEmpty(this.name?.trim())) {
      errors.push('Please enter a name');
    }

    return errors.length ? errors : null;
  }

  save = dropTask(async (event: Event) => {
    try {
      event.preventDefault();

      await this.args.onSave(this.name, this.description);
      let type = this.args.campaign.isApiTriggered ? 'Broadcast' : 'Campaign';
      this.alerts.success('campaign.edit.success', `${type} details updated!`);

      this.isEditing = false;
    } catch (error) {
      this.apiErrors.report(error);
    }
  });

  @action
  edit() {
    if (!this.auth.hasPermission('journeys/campaigns/edit')) {
      return;
    }

    this.name = this.args.campaign.name;
    this.description = this.args.campaign.description;
    this.isEditing = true;
  }

  @action
  cancel() {
    this.isEditing = false;

    // If no edits have been made, we don't want to send a discard event.
    if (this.name === this.args.campaign.name && this.description === this.args.campaign.description) {
      return;
    }

    this.analytics.track('campaign', 'edit_discarded', {
      model: this.args.campaign,
      from: 'name_and_description',
    });
  }
}
